import apiClient from './api';
import { Building, Theme } from '../types';

export interface MemberInfo {
  name: string;
  profileImage: string | null;
  department: string;
  studentId: string;
  university: {
    id: number;
    name: string;
  };
}

export interface UserRegistration {
  email: string;
  password: string;
  name: string;
  profileImageUrl?: string;
  studentId?: string;
  universityId: number;
  departmentId: number;
  verificationToken: string;
}

export interface ImageUploadResponse {
  url: string;
}

export interface FindEmailRequest {
  universityId: number;
  studentId: string;
  name: string;
}

export interface FindEmailResponse {
  email: string;
  createdAt: string;
}

export const memberService = {
  getMe: async (): Promise<MemberInfo> => {
    const response = await apiClient.get<MemberInfo>('/me');
    return response.data;
  },

  getMyBuildings: async (): Promise<Building[]> => {
    const universityId = localStorage.getItem('universityId');
    if (!universityId) {
      throw new Error('University ID not found in localStorage');
    }
    const response = await apiClient.get<Building[]>(`/universities/${universityId}/buildings`);
    return response.data;
  },

  getMyThemes: async (): Promise<Theme[]> => {
    const universityId = localStorage.getItem('universityId');
    if (!universityId) {
      throw new Error('University ID not found in localStorage');
    }
    const response = await apiClient.get<Theme[]>(`/universities/${universityId}/themes`);
    return response.data;
  },

  getBuilding: async (buildingId: number): Promise<Building> => {
    const response = await apiClient.get<Building>(`/buildings/${buildingId}`);
    return response.data;
  },

  uploadProfileImage: async (file: File): Promise<ImageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ImageUploadResponse>('/members/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  register: async (userData: UserRegistration): Promise<void> => {
    const registerRequest = {
      email: userData.email,
      password: userData.password,
      name: userData.name,
      profileImageUrl: userData.profileImageUrl,
      studentId: userData.studentId,
      universityId: userData.universityId,
      departmentId: userData.departmentId,
      verificationToken: userData.verificationToken
    };

    const response = await apiClient.post('/members/register', registerRequest);
    return response.data;
  },

  updateProfileImage: async (profileImage: string): Promise<void> => {
    const response = await apiClient.put('/members/profile-image', {
      profileImage
    });
    return response.data;
  },

  updatePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    await apiClient.put('/members/password', {
      oldPassword,
      newPassword
    });
  },

  withdraw: async (password: string): Promise<void> => {
    await apiClient.post('/members/withdraw', {
      password
    });
  },

  findEmail: async (request: FindEmailRequest): Promise<FindEmailResponse> => {
    const response = await apiClient.post<FindEmailResponse>('/members/find-email', {
      universityId: request.universityId,
      studentId: request.studentId,
      name: request.name
    });
    return response.data;
  },
};
